/**
 * Shared Flapping Types
 *
 * Types for server-side encoder flapping detection and persistence.
 */

// ============================================================================
// Configuration
// ============================================================================

export const FLAPPING_CONFIG = {
  /** Detection window in ms (60 seconds) */
  WINDOW_MS: 60_000,
  /** Number of cycles to trigger warning state */
  WARNING_CYCLES: 2,
  /** Number of cycles to trigger alert state */
  ALERT_CYCLES: 3,
  /** Auto-resolve after this duration of stability (10 minutes) */
  RESOLVE_AFTER_MS: 10 * 60 * 1000,
  /** How long to keep flapping history in SQLite (1 hour) */
  HISTORY_RETENTION_MS: 60 * 60 * 1000,
  /** How often to run cleanup (5 minutes) */
  CLEANUP_INTERVAL_MS: 5 * 60 * 1000,
  /** How often to check for auto-resolution (60 seconds) */
  RESOLUTION_CHECK_INTERVAL_MS: 60_000,
  /** Deduplication window for same-type events (ms)
   * Upstream sometimes sends multiple disconnect events in quick succession.
   * Observed gaps: 893ms, 1151ms - using 1500ms for safety margin */
  DEDUP_WINDOW_MS: 1500,
} as const;

// ============================================================================
// Types
// ============================================================================

export type FlappingSeverity = 'warning' | 'alert';

/** Individual connect/disconnect event */
export interface FlappingEventRecord {
  type: 'connect' | 'disconnect';
  timestamp: number;
}

/** Active flapping alert payload (broadcast via SSE) */
export interface FlappingAlertPayload {
  encoderId: string;
  simpleName: string;
  clientId: string;
  severity: FlappingSeverity;
  cycles: number;
  lastEventType: 'connect' | 'disconnect';
  online: boolean;
  events: FlappingEventRecord[];
  detectedAt: number;
  /** True if this is a disconnect during an active live stream (not flapping cycles) */
  isLiveStreamDisconnect?: boolean;
}

/** Flapping resolved payload (broadcast via SSE) */
export interface FlappingResolvedPayload {
  encoderId: string;
}

/** Persisted flapping record from SQLite */
export interface FlappingHistoryRecord {
  id: number;
  encoderId: string;
  simpleName: string | null;
  clientId: string | null;
  severity: string;
  cycles: number;
  detectedAt: number;
}

/** API response for GET /api/encoders/flapping */
export interface GetFlappingResponse {
  active: FlappingAlertPayload[];
  history?: FlappingHistoryRecord[];
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Check if an event should be considered a duplicate and skipped.
 * Returns true if the event is the same type as the last event and within DEDUP_WINDOW_MS.
 * Upstream (encoder/backend) sometimes sends multiple disconnect events in quick succession.
 */
export function isDuplicateEvent(
  eventType: 'connect' | 'disconnect',
  eventTimestamp: number,
  lastEvent: FlappingEventRecord | undefined
): boolean {
  if (!lastEvent) return false;
  if (lastEvent.type !== eventType) return false;

  // Use absolute value to handle out-of-order events
  // (events may arrive with timestamps slightly out of order)
  const timeDiff = Math.abs(eventTimestamp - lastEvent.timestamp);
  return timeDiff < FLAPPING_CONFIG.DEDUP_WINDOW_MS;
}
